import { useState } from 'react';
import { TextField } from '@material-ui/core';
import type { Control } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { matchSorter } from 'match-sorter';
import type { FilterOptionsState } from '@material-ui/lab';
import globalUseStyles from '../../../globalClasses';

interface ReactHookFormAutoCompleteProps {
  name: string;
  id: string;
  label: string;
  control: Control;
  options: { label: string, value: string }[];
  required?: boolean;
  defaultValue?: { label: string, value: string };
  onBlurFunction: () => Promise<void>
  checkMarkingBounds?: (jewelryType: string, fieldName: string, currentValue: number) => boolean;
  jewelryType?: string;
}

interface IFilterOption {
  label: string;
  value: string;
}

const ReactHookFormAutoComplete: React.FC<ReactHookFormAutoCompleteProps> = ({
  name,
  id,
  label,
  control,
  options,
  defaultValue,
  required,
  onBlurFunction,
  checkMarkingBounds,
  jewelryType,
}) => {
  const filterOptions = (opts: IFilterOption[], state: FilterOptionsState<IFilterOption>) : IFilterOption[] => matchSorter(opts, state.inputValue, {
    keys: [
      { threshold: matchSorter.rankings.WORD_STARTS_WITH, key: 'label' },
    ],
  });

  const [autoCompleteCurrentValue, setAutoCompleteCurrentValue] = useState<{
    label: string;
    value: string;
  } | undefined>(defaultValue || undefined);

  const globalStyles = globalUseStyles();
  const [text, setText] = useState<string>(defaultValue?.label || '');
  const [outOfBounds, setOutOfBounds] = useState(false);

  return (
    <Controller
      render={
      ({ field, fieldState, formState }) => (
        <Autocomplete
          filterOptions={filterOptions}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...field}
          inputValue={text}
          id={id}
          options={options}
          autoComplete
          autoSelect={!autoCompleteCurrentValue}
          autoHighlight
          openOnFocus
          clearOnBlur
          className={outOfBounds ? globalStyles.styleOutOfBounds : ''}
          onHighlightChange={(e, o, r) => {
            if (r === 'auto' && o) {
              setAutoCompleteCurrentValue(o);
            } else if (r === 'keyboard' && o) {
              console.log({ o });
              setAutoCompleteCurrentValue(o);
            } else if (r === 'mouse' && o && autoCompleteCurrentValue === undefined) {
              setAutoCompleteCurrentValue(o);
            }
          }}
          getOptionSelected={(option, value) => option?.value === value?.value}
          getOptionLabel={(option: { label: string, value: string }) => option.label}
          onInputChange={(event, newInputValue) => {
            setText(newInputValue);
            if (jewelryType) {
              const isOutOfBounds = checkMarkingBounds?.(jewelryType, label, Number.parseFloat(newInputValue));
              setOutOfBounds(isOutOfBounds || false);
            }
          }}
          onChange={(e, data, r) => {
            if (r !== 'clear') {
              if (e.type === 'click') {
                field.onChange(data);
                if (data) {
                  setText(data.label);
                }
              } else {
                field.onChange(autoCompleteCurrentValue);
                if (autoCompleteCurrentValue) {
                  setText(autoCompleteCurrentValue.label);
                }
              }
              setAutoCompleteCurrentValue(undefined);
            } else {
              field.onChange(undefined);
              setAutoCompleteCurrentValue(undefined);
              setText('');
            }
          }}
          onClose={(e, r) => {
            if (r === 'blur' && e.type !== 'click' && autoCompleteCurrentValue) {
              field.onChange(autoCompleteCurrentValue);
              if (autoCompleteCurrentValue) {
                setText(autoCompleteCurrentValue.label);
              }
              setAutoCompleteCurrentValue(undefined);
            }
          }}
          onBlur={onBlurFunction}
          renderInput={(params) => (
            <TextField
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...params}
              label={label}
              error={!!fieldState.error || (formState.isSubmitted && required && !fieldState.isDirty && defaultValue === null)}
              helperText={fieldState?.error ? fieldState?.error.message || `${label} is required` : null}
              fullWidth
              required={required}
              inputProps={{
                // eslint-disable-next-line react/jsx-props-no-spreading
                ...params.inputProps,
              }}
              variant="outlined"
            />
          )}
        />
      )
    }
      rules={{ required: !!required }}
      shouldUnregister
      name={name}
      control={control}
      defaultValue={defaultValue}
    />
  );
};

export default ReactHookFormAutoComplete;
